import React, { useState } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import DownloadApp from '@/components/shared/DownloadApp';
import { 
  Calendar,
  Users,
  Wrench,
  Package,
  FileText,
  CreditCard,
  Bell,
  MessageSquare,
  Smartphone,
  Clock,
  CheckCircle,
  ArrowRight,
  Star,
  Zap,
  TrendingUp,
  Shield,
  BarChart3,
  Settings,
  Mail,
  PhoneCall,
  MapPin,
  Globe
} from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faMobile, faUser, faStickyNote, faBuilding } from '@fortawesome/free-solid-svg-icons';

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
  benefits: string[];
}

interface SolutionOption {
  title: string;
  description: string;
  features: string[];
  cta: string;
  ctaLink: string;
  highlighted?: boolean;
}

const MechanicSoftwarePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessName: '',
    interest: 'whitelabel',
    subject: '',
    message: ''
  });

  const features: Feature[] = [
    {
      icon: <Calendar className="w-12 h-12" />,
      title: "Online Scheduling & Booking",
      description: "Let customers book appointments 24/7 from any device",
      benefits: [
        "Real-time availability calendar",
        "Automated appointment confirmations",
        "SMS & email reminders",
        "Online rescheduling & cancellations",
        "Multi-bay/technician scheduling",
        "Customer self-service booking portal"
      ]
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: "Customer Relationship Management (CRM)",
      description: "Build lasting relationships with comprehensive customer data",
      benefits: [
        "Complete customer profiles & history",
        "Vehicle information & service records",
        "Automated follow-up reminders",
        "Service interval tracking",
        "Customer loyalty programs",
        "Targeted marketing campaigns"
      ]
    },
    {
      icon: <Wrench className="w-12 h-12" />,
      title: "Service & Repair Management",
      description: "Streamline your workshop operations from check-in to completion",
      benefits: [
        "Digital vehicle inspection forms",
        "Job card creation & tracking",
        "Technician assignment & scheduling",
        "Labor time tracking",
        "Quality control checklists",
        "Before/after photo documentation"
      ]
    },
    {
      icon: <Package className="w-12 h-12" />,
      title: "Parts Ordering & Inventory",
      description: "Never run out of essential parts with smart inventory management",
      benefits: [
        "Real-time parts availability",
        "Automated supplier ordering",
        "Low stock alerts & auto-reordering",
        "Parts pricing & markup management",
        "Supplier catalog integration",
        "Multi-location inventory sync"
      ]
    },
    {
      icon: <Bell className="w-12 h-12" />,
      title: "Customer Communication & Notifications",
      description: "Keep customers informed at every stage of service",
      benefits: [
        "Push notifications on mobile app",
        "Email & SMS status updates",
        "Real-time service progress tracking",
        "Approval requests for additional work",
        "Ready for pickup notifications",
        "Service completion confirmations"
      ]
    },
    {
      icon: <FileText className="w-12 h-12" />,
      title: "Invoicing & Estimates",
      description: "Professional quotes and invoices in seconds",
      benefits: [
        "Digital estimate creation",
        "Itemized invoicing with labor & parts",
        "Multiple payment method support",
        "Tax calculation & compliance",
        "Discount & coupon management",
        "Invoice history & tracking"
      ]
    },
    {
      icon: <CreditCard className="w-12 h-12" />,
      title: "Online Payments",
      description: "Accept payments securely from anywhere",
      benefits: [
        "Credit/Debit card processing",
        "Mobile payment options",
        "Secure payment gateway",
        "Payment plan management",
        "Automated payment reminders",
        "Digital receipt delivery"
      ]
    },
    {
      icon: <BarChart3 className="w-12 h-12" />,
      title: "Analytics & Reporting",
      description: "Make data-driven decisions with powerful insights",
      benefits: [
        "Revenue & profit tracking",
        "Technician performance metrics",
        "Customer retention analytics",
        "Popular service analysis",
        "Inventory turnover reports",
        "Custom report generation"
      ]
    }
  ];

  const solutionOptions: SolutionOption[] = [
    {
      title: "Join CarSparesEzyFind",
      description: "List your mechanic shop on our established platform",
      features: [
        "Instant access to thousands of customers",
        "Website + Android, iPhone & Huawei apps",
        "Online booking system included",
        "Integrated payment processing",
        "Marketing & SEO support",
        "No setup fees - Start immediately",
        "24/7 technical support",
        "Analytics dashboard"
      ],
      cta: "Join the Marketplace",
      ctaLink: "https://www.CarSparesEzyFind.co.za/list-business"
    },
    {
      title: "WhiteLabel Solution",
      description: "Your own branded mechanic management platform",
      features: [
        "Fully branded website & mobile apps",
        "Android, iPhone & Huawei native apps",
        "Complete mechanic shop management system",
        "All features listed above included",
        "Your own domain & branding",
        "48-hour setup guarantee",
        "Unlimited customers & bookings",
        "Dedicated account manager"
      ],
      cta: "Get Custom Quote",
      ctaLink: "#whitelabel-quote",
      highlighted: true
    }
  ];

  const serviceStages = [
    {
      stage: "Booking Confirmed",
      icon: <CheckCircle className="w-6 h-6" />,
      notification: "Customer receives confirmation with appointment details"
    },
    {
      stage: "Vehicle Checked In",
      icon: <Clock className="w-6 h-6" />,
      notification: "Push notification: 'Your vehicle has been checked in'"
    },
    {
      stage: "Inspection Complete",
      icon: <Wrench className="w-6 h-6" />,
      notification: "Email with inspection report and quote for approval"
    },
    {
      stage: "Work in Progress",
      icon: <Settings className="w-6 h-6" />,
      notification: "Regular updates on repair progress"
    },
    {
      stage: "Quality Check",
      icon: <Shield className="w-6 h-6" />,
      notification: "Final inspection and photos sent to customer"
    },
    {
      stage: "Ready for Pickup",
      icon: <Bell className="w-6 h-6" />,
      notification: "Push notification & SMS: 'Your vehicle is ready!'"
    },
    {
      stage: "Payment Complete",
      icon: <CreditCard className="w-6 h-6" />,
      notification: "Digital receipt and invoice sent via email"
    }
  ];

  const stats = [
    { value: "5,000+", label: "Active Workshops" },
    { value: "100K+", label: "Monthly Bookings" },
    { value: "98%", label: "Customer Satisfaction" },
    { value: "48hrs", label: "Setup Time" }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    alert('Thank you! We will contact you within 24 hours with your custom quote.');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <>
      <Head>
        <title>Mechanic & Auto Repair Shop Software | CarSparesEzyFind</title>
        <meta name="description" content="Complete mechanic shop management software with online booking, CRM, parts ordering, inventory, invoicing, customer notifications, and mobile apps for Android, iPhone & Huawei." />
        <meta name="keywords" content="mechanic software, auto repair shop software, garage management system, online booking, automotive CRM, workshop management" />
      </Head>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-orange-600 via-red-600 to-red-700 text-white py-20 overflow-hidden">
          <div className="absolute inset-0 bg-black opacity-10"></div>
          <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <Wrench className="w-4 h-4 mr-2 text-yellow-300" />
                <span className="text-sm font-semibold">Complete Mechanic & Auto Repair Shop Solution</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Transform Your <span className="text-yellow-300">Auto Repair Shop</span>
              </h1>
              
              <p className="text-xl md:text-2xl mb-8 text-orange-100">
                Online Booking • CRM • Inventory • Parts Ordering • Customer Notifications • Invoicing
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <a 
                  href="https://www.CarSparesEzyFind.co.za/list-business" 
                  className="bg-yellow-400 hover:bg-yellow-300 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl flex items-center justify-center"
                >
                  Join Our Platform
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
                <a 
                  href="#whitelabel-quote" 
                  className="bg-white hover:bg-gray-100 text-red-700 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl flex items-center justify-center border-2 border-white"
                >
                  Get Your Own Platform
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
                {stats.map((stat, index) => (
                  <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <div className="text-3xl font-bold text-yellow-300">{stat.value}</div>
                    <div className="text-sm text-orange-100 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Mobile Apps Highlight */}
        <section className="py-12 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-center gap-8 text-center md:text-left">
              <Smartphone className="w-16 h-16 text-yellow-300" />
              <div>
                <h3 className="text-2xl font-bold mb-2">Available on All Major Platforms</h3>
                <p className="text-blue-100">Website + Mobile Apps for Android, iPhone & Huawei</p>
              </div>
              <div className="flex gap-4">
                <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg font-semibold">Android</div>
                <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg font-semibold">iPhone</div>
                <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg font-semibold">Huawei</div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Everything Your Workshop Needs
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive software solution designed specifically for mechanic shops and auto repair centers
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl p-8 hover:shadow-2xl transition-all hover:border-orange-500 transform hover:-translate-y-2"
                >
                  <div className="text-orange-600 mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {feature.description}
                  </p>
                  <ul className="space-y-2">
                    {feature.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Customer Journey Section */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Bell className="w-16 h-16 text-orange-600 mx-auto mb-4" />
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Real-Time Customer Communication
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Keep customers informed at every stage with automated push notifications and email updates
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="relative">
                {/* Progress Line */}
                <div className="absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-orange-500 to-red-500 hidden md:block"></div>

                {serviceStages.map((stage, index) => (
                  <div key={index} className="relative mb-8 md:ml-20">
                    <div className="absolute -left-20 top-2 hidden md:block">
                      <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white shadow-lg">
                        {stage.icon}
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200 hover:border-orange-500 transition-all">
                      <div className="flex items-start gap-4">
                        <div className="md:hidden text-orange-600">{stage.icon}</div>
                        <div className="flex-1">
                          <h4 className="text-xl font-bold text-gray-900 mb-2">{stage.stage}</h4>
                          <div className="flex items-start gap-2">
                            <Bell className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                            <p className="text-gray-600">{stage.notification}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Solution Options Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Choose Your Solution
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Join our established platform or get your own branded solution
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {solutionOptions.map((option, index) => (
                <div 
                  key={index}
                  className={`relative rounded-2xl p-8 ${
                    option.highlighted 
                      ? 'bg-gradient-to-br from-orange-600 to-red-700 text-white shadow-2xl transform scale-105' 
                      : 'bg-white border-2 border-gray-200'
                  }`}
                >
                  {option.highlighted && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-bold">
                        RECOMMENDED
                      </span>
                    </div>
                  )}

                  <div className="mb-6">
                    <h3 className={`text-3xl font-bold mb-2 ${option.highlighted ? 'text-white' : 'text-gray-900'}`}>
                      {option.title}
                    </h3>
                    <p className={option.highlighted ? 'text-orange-100' : 'text-gray-600'}>
                      {option.description}
                    </p>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {option.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className={`w-5 h-5 mr-3 mt-0.5 flex-shrink-0 ${
                          option.highlighted ? 'text-yellow-300' : 'text-green-500'
                        }`} />
                        <span className={option.highlighted ? 'text-orange-50' : 'text-gray-700'}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <a 
                    href={option.ctaLink}
                    className={`block text-center px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 ${
                      option.highlighted
                        ? 'bg-yellow-400 hover:bg-yellow-300 text-gray-900'
                        : 'bg-orange-600 hover:bg-orange-700 text-white'
                    }`}
                  >
                    {option.cta}
                    <ArrowRight className="inline-block ml-2 w-5 h-5" />
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WhiteLabel Quote Section */}
        <section id="whitelabel-quote" className="py-20 bg-gradient-to-br from-gray-900 to-red-900 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <Zap className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                <h2 className="text-4xl md:text-5xl font-bold mb-4">
                  Get Your WhiteLabel Mechanic Platform
                </h2>
                <p className="text-xl text-orange-100">
                  Custom branded website & mobile apps - Ready in 48 hours!
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8 mb-12">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <h3 className="text-2xl font-bold mb-4 flex items-center">
                    <CheckCircle className="w-6 h-6 text-green-400 mr-2" />
                    What's Included
                  </h3>
                  <ul className="space-y-3">
                    {[
                      "Fully branded website with your logo",
                      "Android, iPhone & Huawei mobile apps",
                      "Online booking & scheduling system",
                      "Complete CRM & customer management",
                      "Parts ordering & inventory control",
                      "Invoicing & payment processing",
                      "Push notifications & email alerts",
                      "Analytics & reporting dashboard",
                      "Your own domain name",
                      "Unlimited bookings & customers"
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <ArrowRight className="w-5 h-5 text-yellow-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <h3 className="text-2xl font-bold mb-4 flex items-center">
                    <Clock className="w-6 h-6 text-blue-400 mr-2" />
                    Quick Setup Process
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="bg-orange-500 rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0 font-bold">1</div>
                      <div>
                        <h4 className="font-bold mb-1">Submit Your Details</h4>
                        <p className="text-orange-100 text-sm">Fill out the form below with your business information</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-orange-500 rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0 font-bold">2</div>
                      <div>
                        <h4 className="font-bold mb-1">Receive Custom Quote</h4>
                        <p className="text-orange-100 text-sm">Get a detailed proposal within 24 hours</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-orange-500 rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0 font-bold">3</div>
                      <div>
                        <h4 className="font-bold mb-1">Launch Your Platform</h4>
                        <p className="text-orange-100 text-sm">Your branded solution goes live in 48 hours</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-yellow-400/20 border-2 border-yellow-400 rounded-lg">
                    <p className="font-bold text-yellow-300">
                      ⚡ Special Offer: First 10 workshops get 25% off!
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-white rounded-2xl p-8 md:p-12 text-gray-900">
                <h3 className="text-3xl font-bold text-center mb-8">Request Your Custom Quote</h3>
                
                <form onSubmit={handleSubmit}>
                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>Name *</label>
                    <div className='relative'>
                      <input 
                        type='text' 
                        name='name' 
                        placeholder='Enter name' 
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-orange-500 focus:outline-none' 
                      />
                      <FontAwesomeIcon icon={faUser} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                    </div>
                  </div>

                  <div className='flex flex-col md:flex-row md:gap-5'>
                    <div className='mb-4 w-full'>
                      <label className='mb-2 block font-semibold'>Phone *</label>
                      <div className='relative'>
                        <div className='flex'>
                          <input 
                            type='text' 
                            name='code' 
                            placeholder='27' 
                            value={'+27'} 
                            disabled 
                            className='form-control border border-gray-300 border-r-0 text-sm xl:text-[14px] 2xl:text-md h-10 px-3 w-[50px] rounded-l' 
                          />
                          <input 
                            type='text' 
                            name='phone' 
                            placeholder='Enter mobile number' 
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded-r focus:border-orange-500 focus:outline-none' 
                          />
                        </div>
                        <FontAwesomeIcon icon={faMobile} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                      </div>
                    </div>
                    <div className='mb-4 w-full'>
                      <label className='mb-2 block font-semibold'>Email *</label>
                      <div className='relative'>
                        <input 
                          type='email' 
                          name='email' 
                          placeholder='Enter email Id' 
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-orange-500 focus:outline-none' 
                        />
                        <FontAwesomeIcon icon={faEnvelope} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                      </div>
                    </div>
                  </div>

                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>Workshop/Business Name *</label>
                    <div className='relative'>
                      <input 
                        type='text' 
                        name='businessName' 
                        placeholder='Enter workshop name' 
                        value={formData.businessName}
                        onChange={handleChange}
                        required
                        className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-orange-500 focus:outline-none' 
                      />
                      <FontAwesomeIcon icon={faBuilding} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                    </div>
                  </div>

                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>I'm interested in *</label>
                    <select
                      name='interest'
                      value={formData.interest}
                      onChange={handleChange}
                      className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-orange-500 focus:outline-none'
                    >
                      <option value="whitelabel">WhiteLabel Solution</option>
                      <option value="join">Joining CarSparesEzyFind</option>
                      <option value="both">Both Options - Need More Info</option>
                    </select>
                  </div>

                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>Subject</label>
                    <div className='relative'>
                      <input 
                        type='text' 
                        name='subject' 
                        placeholder='Subject' 
                        value={formData.subject}
                        onChange={handleChange}
                        className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-orange-500 focus:outline-none' 
                      />
                      <FontAwesomeIcon icon={faStickyNote} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                    </div>
                  </div>

                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>Message</label>
                    <textarea 
                      name='message'
                      placeholder='Tell us about your workshop and requirements' 
                      rows={5} 
                      value={formData.message}
                      onChange={handleChange}
                      className='form-control py-3 border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full px-3 rounded focus:border-orange-500 focus:outline-none'
                    />
                  </div>

                  <div className='text-right'>
                    <button 
                      type="submit"
                      className='bg-orange-600 text-white border border-orange-600 uppercase transition-all hover:bg-white hover:text-orange-600 px-10 py-3 rounded font-bold'
                    >
                      Get My Quote
                    </button>
                  </div>
                </form>

                <p className="text-center text-sm text-gray-600 mt-6">
                  🔒 Your information is secure. We'll contact you within 24 hours with a detailed quote.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 bg-gradient-to-r from-orange-600 to-red-700 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Digitize Your Mechanic Shop?
            </h2>
            <p className="text-xl text-orange-100 mb-8 max-w-3xl mx-auto">
              Join thousands of successful auto repair shops using our platform to grow their business
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://www.CarSparesEzyFind.co.za/list-business"
                className="bg-white hover:bg-gray-100 text-red-700 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center"
              >
                Join CarSparesEzyFind
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
              <a 
                href="#whitelabel-quote"
                className="bg-yellow-400 hover:bg-yellow-300 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center"
              >
                Get WhiteLabel Solution
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </div>
          </div>
        </section>
      </div>
         <DownloadApp/>
    </>
  );
};

export default MechanicSoftwarePage;